import Chart from 'react-apexcharts'
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation'
import { useCallback, useEffect, useState } from 'react'
import { twinFetchPostJSON } from '../../../../utils/globals/data'
import { QuoteStatusPercentageData, QuoteStatusPercentageDataKeys } from './types'
import './quotestatuspercentage.sass'
import useDictMonths from '../../../../utils/hooks/useDictMonths'
import { quoteOrderStatus, QuoteOrderStatusInversed, QuoteOrderStatusKeys } from '@teinor/erp/types/company/order/quote/quoteOrder'

interface DSBQuoteStatusPercentageProps extends DSBQuoteStatusPercentageLogicProps {
    title?: string
}

const DSBQuoteStatusPercentage = ({title, end_date, start_date }: DSBQuoteStatusPercentageProps) => {
    const { t } = useTwinTranslation()
    const {quotes} = useDSBQuoteStatusPercentageLogic({end_date, start_date})
    const {arrMonthShort} = useDictMonths()
    const customlegendFormatter = (name: any) => {
        return `<div class='legend-label'>${name}</div>`
    }
    const series = [
        {
            name: t('pending', 'Pendientes'),
            type: 'column',
            data: quotes.waitingResponse
        },
        {
            name: t('sent', 'Enviados'),
            type: 'column',
            data: quotes.sent
        },
        {
            name: t('accepted', 'Aceptados'),
            type: 'column',
            data: quotes.accepted
        },
        {
            name: t('cancelleds', 'Cancelados'),
            type: 'column',
            data: quotes.denied
        },
    ]
    return (
        <div className='flex flex-col flex-auto h-1 gap-10 quote_status_percentage'>
            <h3>{title}</h3>
            <div className='flex-auto h-1'>
                <Chart options={{
                    chart: {
                        type: 'bar',
                        stacked: true,
                        events: {
                            mounted: (chart) => {
                                chart.windowResizeHandler()
                            }
                        },
                        toolbar: {
                            show: false
                        },
                        zoom: {
                            enabled: true
                        }
                    },
                    plotOptions: {
                        bar: {
                            horizontal: false,
                            columnWidth: '100%',
                        },
                    },
                    stroke: {
                        show: true,
                        width: 2,
                        colors: ['transparent']
                    },
                    yaxis: {
                        labels: {
                            style: {
                                colors: '#8F9BA4',
                                fontFamily: 'Roboto'
                            }
                        },
                        max: 100,
                    },
                    xaxis: {
                        labels: {
                            style: {
                                fontFamily: 'Roboto',
                                colors: '#8F9BA4',
                            }
                        },
                        categories: arrMonthShort
                    },
                    fill: {
                        opacity: 1,
                    },
                    legend: {
                        position: 'top',
                        horizontalAlign: 'left',
                        formatter: customlegendFormatter,
                        fontFamily: 'Roboto',
                    },
                    colors: ['#FFA200', '#865FE5', '#1DB27B', '#EB2D53'],
                    tooltip: {
                        y: {
                            formatter: function (val) {
                                return val + "%"
                            }
                        }
                    }
                }} series={series} type="bar" height={'100%'} />
            </div>
        </div>
    )
}

interface DSBQuoteStatusPercentageLogicProps {
    start_date: Date
    end_date: Date
}

const useDSBQuoteStatusPercentageLogic = ({end_date, start_date}: DSBQuoteStatusPercentageLogicProps) => {
    const [quotes, setQuotes] = useState<QuoteStatusPercentageData>({ accepted: [], denied: [], sent: [], waitingResponse: [] })

    const getData = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/order/quote/getAllQuoteOrdersForStats', {
            attributes: ['status', 'createdAt'],
            where: {
                'QuoteOrder.createdAt': { '$betweenN': [start_date, end_date] }, status: { '$notN': QuoteOrderStatusInversed['creating'] }, is_last: true}
        })
        const totals = Array(12).fill(0)
        const quoteStatus: QuoteStatusPercentageData = { accepted: Array(12).fill(0), denied: Array(12).fill(0), sent: Array(12).fill(0), waitingResponse: Array(12).fill(0) }

        if (result) {
            for (const element of result) {
                const date = new Date(element.createdAt)
                const month = date.getMonth()
                totals[month] += 1
                let myStatusInversed = quoteOrderStatus[element.status as QuoteOrderStatusKeys] as QuoteStatusPercentageDataKeys
                if (element.status === QuoteOrderStatusInversed['processed']) {
                    myStatusInversed = 'accepted'
                }
                quoteStatus[myStatusInversed][month] += 1
            }
            for (let key in totals) {
                quoteStatus.accepted[key] = quoteStatus.accepted[key] ? parseInt(String((quoteStatus.accepted[key] / totals[key]) * 100)) : 0
                quoteStatus.denied[key] = quoteStatus.denied[key] ? parseInt(String((quoteStatus.denied[key] / totals[key]) * 100)) : 0
                quoteStatus.sent[key] = quoteStatus.sent ? parseInt(String((quoteStatus.sent[key] / totals[key]) * 100)) : 0
                quoteStatus.waitingResponse[key] = quoteStatus.waitingResponse ? parseInt(String((quoteStatus.waitingResponse[key] / totals[key]) * 100)) : 0
            }
            setQuotes(quoteStatus)
        }
    }, [setQuotes, end_date, start_date])

    useEffect(() => {
        getData()
    }, [getData])

    return {quotes}
}


export default DSBQuoteStatusPercentage